"use client";

import React from "react";
import Link from "next/link";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import { H6, Paragraph } from "src/lib/components/salify-next-layout/default/components/Typography";
import { FlexBox } from "src/lib/components/salify-next-layout/default/components/flex-box";
import FacebookIcon from "@mui/icons-material/Facebook";
import TwitterIcon from "@mui/icons-material/Twitter";
import YouTubeIcon from "@mui/icons-material/YouTube";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import { layoutConstant } from "src/lib/utils/constants";
import { toAssetImages } from "src/lib/utils";
import Image from "next/image";

export interface VariantProps {
  logoUrl: string;
  logoWidth: number;
  logoHeight: number;
  backgroundImage?: string;
  companyName: string;
  companyAddress: string;
  companyPhone: string;
  companyMobile: string;
  companyEmail: string;
  products: Array<{ label: string; url: string }>;
  solutions: Array<{ label: string; url: string }>;
  aboutUs: Array<{ label: string; url: string }>;
  socialItems: Array<{
    type: "facebook" | "twitter" | "youtube" | "linkedin";
    url: string;
  }>;
  connectText?: string;
  copyright?: string;
  policies?: Array<{ label: string; url: string }>;
}

const ICON_MAPPING: {
  [key in "facebook" | "twitter" | "youtube" | "linkedin"]: any;
} = {
  facebook: FacebookIcon,
  twitter: TwitterIcon,
  youtube: YouTubeIcon,
  linkedin: LinkedInIcon,
};

const FooterHaiAnh: React.FC<VariantProps> = (props) => {
  const SocialLinks = () => {
    if (!props.socialItems.length) {
      return null;
    }
    return (
      <FlexBox className="flex" sx={{ gap: { xs: 1, sm: 0.5 } }}>
        {props.socialItems.map(({ type, url }, ind) => {
          const Icon = (ICON_MAPPING as any)[type];
          return Icon ? (
            <Link
              href={url}
              key={ind}
              style={{
                color: "#cccccc",
                padding: "8px",
                minWidth: "40px",
                minHeight: "40px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Icon sx={{ fontSize: { xs: 24, sm: 20 } }} />
            </Link>
          ) : null;
        })}
      </FlexBox>
    );
  };

  return (
    <Box
      component="footer"
      bgcolor="#1a1a1a"
      color="white"
      sx={{
        backgroundImage: props.backgroundImage ? `url(${props.backgroundImage})` : "none",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
      mb={0}
    >
      <Box
        component={Container}
        sx={{
          px: { xs: 2, sm: 3, md: 4, lg: 6 }, // Responsive horizontal padding
          pt: { xs: 4, sm: 8 },
          pb: { xs: 2, sm: 4 },
        }}
        overflow="hidden"
      >
        <Grid container spacing={{ xs: 2, sm: 3 }}>
          {/* COMPANY INFO SECTION */}
          <Grid item lg={3} md={6} sm={6} xs={6}>
            <Link href="/">
              {props.logoUrl && (
                <Image
                  src={toAssetImages(props.logoUrl)}
                  alt="Company Logo"
                  width={props.logoWidth ?? 160}
                  height={props.logoHeight ?? 48}
                />
              )}
            </Link>

            <Box mt={{ xs: 2, sm: 3 }}>
              <Paragraph
                py={0.6}
                sx={{
                  color: "white",
                  fontSize: { xs: "16px", sm: "14px" },
                  fontWeight: 600,
                }}
              >
                {props.companyName}
              </Paragraph>

              <Paragraph
                py={0.6}
                sx={{
                  color: "#cccccc",
                  fontSize: { xs: "14px", sm: "13px" },
                  lineHeight: 1.6,
                }}
                dangerouslySetInnerHTML={{ __html: props.companyAddress }}
              ></Paragraph>

              <Paragraph
                py={0.3}
                sx={{
                  color: "#cccccc",
                  fontSize: { xs: "14px", sm: "13px" },
                }}
              >
                • {props.companyPhone}
              </Paragraph>

              <Paragraph
                py={0.3}
                sx={{
                  color: "#cccccc",
                  fontSize: { xs: "14px", sm: "13px" },
                }}
              >
                • {props.companyMobile}
              </Paragraph>

              <Paragraph
                py={0.3}
                sx={{
                  color: "#cccccc",
                  fontSize: { xs: "14px", sm: "13px" },
                }}
              >
                • {props.companyEmail}
              </Paragraph>
            </Box>

            {/* Working Hours Section */}
            {(props as any).workingHours && (
              <Box mt={{ xs: 2, sm: 2.5 }}>
                <H6 fontSize={{ xs: 16, sm: 14 }} mb={1} sx={{ color: "white", fontWeight: 600 }}>
                  Thời gian làm việc
                </H6>
                <Paragraph
                  py={0.3}
                  sx={{
                    color: "#cccccc",
                    fontSize: { xs: "14px", sm: "13px" },
                    lineHeight: 1.6,
                  }}
                  dangerouslySetInnerHTML={{ __html: (props as any).workingHours }}
                />
              </Box>
            )}
          </Grid>

          {/* RIGHT COLUMN - Navigation Links */}
          <Grid item lg={9} md={6} sm={6} xs={6}>
            <Grid container spacing={{ xs: 1.5, sm: 2 }}>
              {/* PRODUCTS SECTION */}
              <Grid item lg={4} md={12} sm={12} xs={12}>
                <H6
                  fontSize={{ xs: 16, sm: 18 }}
                  mb={1.5}
                  lineHeight="1"
                  sx={{ color: "white", fontWeight: 600 }}
                >
                  Sản phẩm
                </H6>

                {props.products.map((item, ind) => (
                  <Paragraph key={ind} py={{ xs: 0.4, sm: 0.5 }}>
                    <Link
                      href={item.url}
                      style={{
                        color: "#cccccc",
                        textDecoration: "none",
                        fontSize: "14px",
                        display: "block",
                        lineHeight: "1.5",
                      }}
                    >
                      {item.label}
                    </Link>
                  </Paragraph>
                ))}
              </Grid>

              {/* SOLUTIONS SECTION */}
              <Grid item lg={4} md={12} sm={12} xs={12}>
                <H6
                  fontSize={{ xs: 16, sm: 18 }}
                  mb={1.5}
                  lineHeight="1"
                  sx={{ color: "white", fontWeight: 600 }}
                >
                  Giải pháp
                </H6>

                {props.solutions.map((item, ind) => (
                  <Paragraph key={ind} py={{ xs: 0.4, sm: 0.5 }}>
                    <Link
                      href={item.url}
                      style={{
                        color: "#cccccc",
                        textDecoration: "none",
                        fontSize: "14px",
                        display: "block",
                        lineHeight: "1.5",
                      }}
                    >
                      {item.label}
                    </Link>
                  </Paragraph>
                ))}
              </Grid>

              {/* ABOUT US & SOCIAL SECTION */}
              <Grid item lg={4} md={12} sm={12} xs={12}>
                <H6
                  fontSize={{ xs: 16, sm: 18 }}
                  mb={1.5}
                  lineHeight="1"
                  sx={{ color: "white", fontWeight: 600 }}
                >
                  Về chúng tôi
                </H6>

                {props.aboutUs.map((item, ind) => (
                  <Paragraph key={ind} py={{ xs: 0.4, sm: 0.5 }}>
                    <Link
                      href={item.url}
                      style={{
                        color: "#cccccc",
                        textDecoration: "none",
                        fontSize: "14px",
                        display: "block",
                        lineHeight: "1.5",
                      }}
                    >
                      {item.label}
                    </Link>
                  </Paragraph>
                ))}

                <Box mt={{ xs: 2, sm: 3 }}>
                  <Paragraph
                    sx={{
                      color: "white",
                      fontSize: { xs: "16px", sm: "14px" },
                      marginBottom: 1,
                      fontWeight: 600,
                    }}
                  >
                    {props.connectText || "Kết nối với chúng tôi"}
                  </Paragraph>
                  <SocialLinks />
                </Box>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>

      {/* COPYRIGHT SECTION */}
      <Box
        component={Container}
        sx={{
          borderTop: "1px solid #333",
          px: { xs: 2, sm: 3, md: 4, lg: 6 },
          py: { xs: 2, sm: 2 },
        }}
      >
        <Grid container spacing={{ xs: 2, sm: 3 }} alignItems="center">
          <Grid item sm={8} xs={12} textAlign={{ xs: "center", sm: "left" }}>
            <Paragraph
              sx={{
                color: "#cccccc",
                fontSize: { xs: "13px", sm: "14px" },
                lineHeight: 1.5,
              }}
            >
              {props.copyright ||
                "© 2025 Hai Anh Automation Technology Company Limited. All rights reserved."}
            </Paragraph>
          </Grid>
          <Grid item sm={4} xs={12} textAlign={{ xs: "center", sm: "right" }}>
            <Box
              display="flex"
              flexDirection={{ xs: "column", sm: "row" }}
              justifyContent={{ xs: "center", sm: "flex-end" }}
              gap={{ xs: 1, sm: 2 }}
            >
              {props.policies &&
                props.policies.map((policy, ind) => (
                  <Link
                    key={ind}
                    href={policy.url}
                    style={{
                      color: "#cccccc",
                      textDecoration: "none",
                      fontSize: "13px",
                      lineHeight: "1.5",
                    }}
                  >
                    {policy.label}
                  </Link>
                ))}
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default FooterHaiAnh;
